import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { Honeypot } from "../components/Honeypot";
import useReveal from "../hooks/useReveal";
import { useEstimate } from "../components/EstimateContext";
import usePublicData from "../hooks/usePublicData";
import { TESTIMONIALS, COMPANY } from "../mock";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import {
  MessageSquare, PenTool, Layers, FileCheck, Hammer, CheckCircle2, Plus, Minus, ArrowRight,
  Check, Star, Users, Palette, CalendarClock, Wallet, MapPin, Ruler, Clock, Phone,
} from "lucide-react";
import {
  IMG, AREAS, INCLUDED, WHY, PROCESS, PORTFOLIO, CONSIDER, COST_FACTORS, FAQ,
} from "../data/fullHomeData";

const ICONS = { MessageSquare, PenTool, Layers, FileCheck, Hammer, CheckCircle2, Users, Palette, CalendarClock, Wallet };

const Heading = ({ eyebrow, title, sub, center }) => (
  <div className={`${center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"} mb-12 reveal`}>
    <div className={`flex items-center gap-3 mb-4 ${center ? "justify-center" : ""}`}>
      <span className="h-px w-10 bg-[#2f9fe6]" />
      <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">{eyebrow}</span>
      {center && <span className="h-px w-10 bg-[#2f9fe6]" />}
    </div>
    <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight">{title}</h2>
    {sub && <p className="text-[#6b7683] text-lg mt-4">{sub}</p>}
  </div>
);

function FullHomeForm() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [website, setWebsite] = useState("");
  const [form, setForm] = useState({ name: "", contact: "", neighbourhood: "", scope: "", size: "", timeline: "", goals: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full px-4 py-3 rounded-xl bg-white border border-white/20 text-[#0f2740] outline-none focus:ring-2 focus:ring-[#2f9fe6]/50 placeholder:text-[#9aa4af]";

  const submit = async (e) => {
    e.preventDefault();
    const message = [
      `Scope: ${form.scope || "—"}`,
      `Neighbourhood: ${form.neighbourhood || "—"}`,
      `Approx home size: ${form.size || "—"}`,
      `Target timeline: ${form.timeline || "—"}`,
      `Goals: ${form.goals || "—"}`,
    ].join("\n");
    const isEmail = form.contact.includes("@");
    try {
      await api.post("/leads", { name: form.name, email: isEmail ? form.contact : "", phone: isEmail ? "" : form.contact, service: "full-home", message, source: "full-home-page", website });
      setSubmitted(true);
      toast({ title: "Request received", description: "We'll reach out to discuss your whole-home remodel." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us.", variant: "destructive" });
    }
  };

  if (submitted) {
    return (
      <div className="bg-white/10 rounded-2xl p-10 text-center border border-white/15" data-testid="full-home-form-success">
        <CheckCircle2 className="w-14 h-14 text-[#2f9fe6] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Thank you!</h3>
        <p className="text-[#a9c6dd]">Your request has been received. Our team will contact you soon to discuss your Edmonton whole-home remodel.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white/5 rounded-2xl p-6 sm:p-8 border border-white/10 space-y-4" data-testid="full-home-form">
      <Honeypot value={website} onChange={setWebsite} />
      <div className="grid sm:grid-cols-2 gap-4">
        <input required className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" data-testid="fh-name" />
        <input required className={inputCls} value={form.contact} onChange={(e) => set("contact", e.target.value)} placeholder="Phone or email" data-testid="fh-contact" />
        <input className={inputCls} value={form.neighbourhood} onChange={(e) => set("neighbourhood", e.target.value)} placeholder="Edmonton neighbourhood" data-testid="fh-neighbourhood" />
        <select className={inputCls} value={form.scope} onChange={(e) => set("scope", e.target.value)} data-testid="fh-scope">
          <option value="">Scope of work…</option>
          <option>Main floor remodel</option>
          <option>Multiple rooms</option>
          <option>Whole home top-to-bottom</option>
          <option>Not sure yet</option>
        </select>
        <input className={inputCls} value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="Approximate home size (sq ft)" data-testid="fh-size" />
        <input className={inputCls} value={form.timeline} onChange={(e) => set("timeline", e.target.value)} placeholder="Target timeline" data-testid="fh-timeline" />
      </div>
      <textarea rows={3} className={inputCls} value={form.goals} onChange={(e) => set("goals", e.target.value)} placeholder="Your goals (e.g. open up the main floor, new kitchen and bathrooms, cohesive finishes…)" data-testid="fh-goals" />
      <button type="submit" className="w-full py-3.5 rounded-full bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors" data-testid="fh-submit">Request a Detailed Estimate</button>
      <a href={COMPANY.phoneHref} className="flex items-center justify-center gap-2 text-sm text-[#a9c6dd] hover:text-white transition-colors"><Phone className="w-4 h-4" /> Or call us: {COMPANY.phone}</a>
    </form>
  );
}

export default function FullHomeRemodeling() {
  useReveal();
  const { openEstimate } = useEstimate();
  const [openFaq, setOpenFaq] = useState(0);
  const { data: reviews } = usePublicData("/reviews", TESTIMONIALS);
  useReveal([reviews]);

  return (
    <>
      <Seo title="Full Home Remodeling Edmonton" path="/services/full-home"
        description="Whole-home remodeling in Edmonton — one team for your kitchen, bathrooms, living spaces and more, with cohesive design, one schedule and code-compliant construction."
        image={IMG.hero} />

      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Fully remodeled open-concept Edmonton home" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2740]/95 via-[#0f2740]/82 to-[#0f2740]/45" />
        </div>
        <div className="container-x relative z-10 pt-28 pb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#2f9fe6]" />
            <span className="text-[#bfe0f5] tracking-[0.26em] text-xs font-medium uppercase">Full Home Remodeling</span>
          </div>
          <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6 max-w-3xl">
            <span className="font-light">Reimagine</span> <span className="font-bold text-[#2f9fe6]">Your Entire Home</span>, One Team
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mb-9">
            From the kitchen to the last baseboard, we remodel whole Edmonton homes with a single design vision, one coordinated schedule and craftsmanship you can rely on — so every room finally works together.
          </p>
          <div className="flex flex-wrap gap-4">
            <button data-testid="hero-estimate-btn" onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold flex items-center gap-2 hover:bg-[#1f82c7] transition-all">
              Get a Free Estimate <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <Link to="/projects" className="px-8 py-4 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">View Our Work</Link>
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Whole Home" title="Every space, thoughtfully connected" sub="A full remodel touches every room — here's where we bring it all together." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AREAS.map((a, i) => (
              <div key={a.name} className={`reveal delay-${(i % 3) + 1} relative group shape-soft h-72 cursor-pointer`} data-testid={`area-card-${i}`}>
                <img src={a.img} alt={a.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/90 via-[#0f2740]/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h3 className="font-display text-xl font-bold text-white mb-2">{a.name}</h3>
                  <p className="text-white/80 text-sm max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-all duration-500 overflow-hidden">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <div className="shape-arch-top"><img src={IMG.living} alt="Remodeled living space" className="w-full h-[520px] object-cover" /></div>
          </div>
          <div className="reveal-right">
            <Heading eyebrow="Scope" title="What a full home remodel includes" />
            <div className="grid sm:grid-cols-2 gap-3">
              {INCLUDED.map((it) => (
                <div key={it} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-[#eef1f4]">
                  <span className="w-6 h-6 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="text-[#0f2740] text-sm font-medium">{it}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY WHOLE-HOME */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x">
          <div className="max-w-3xl mb-14 reveal">
            <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Why Whole-Home</span></div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">The advantage of doing it together</h2>
            <p className="text-[#a9c6dd] text-lg">Renovating the whole home under one team means a single vision, one schedule and far less disruption than tackling room after room with different contractors.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <div key={w.title} className={`reveal delay-${(i % 4) + 1} bg-white/5 rounded-2xl p-7 border border-white/10`}>
                  <div className="w-12 h-12 rounded-xl bg-[#2f9fe6]/15 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                  <h3 className="font-display text-lg font-semibold mb-2">{w.title}</h3>
                  <p className="text-[#a9c6dd] text-sm leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DESIGN-LED */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <Heading eyebrow="Design-Led" title="One vision across every room" />
            <p className="text-[#5a6570] text-lg leading-relaxed mb-6">
              A whole-home remodel is your chance to fix how the house actually lives — not just how it looks. We start with your day-to-day: where light lands, how you move between rooms, what's cramped and what's wasted.
            </p>
            <p className="text-[#5a6570] leading-relaxed mb-8">
              From there we develop a cohesive plan for layout, flooring, colours, cabinetry and lighting so the finished home feels intentional and unified — never like a series of disconnected projects.
            </p>
            <Link to="/projects" className="inline-flex items-center gap-2 text-[#2f9fe6] font-semibold hover:gap-3 transition-all">
              See recent transformations <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="reveal-right">
            <div className="shape-soft"><img src={IMG.staircase} alt="Refreshed staircase and entry" className="w-full h-[480px] object-cover" /></div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Our Process" title="How your remodel runs" sub="A clear, systematic process so you always know what's next." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROCESS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <div key={p.title} className={`reveal delay-${(i % 3) + 1} bg-[#f7f9fb] rounded-2xl p-7 border border-[#eef1f4]`}>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-12 h-12 rounded-full bg-[#eaf5fd] flex items-center justify-center"><Icon className="w-6 h-6 text-[#2f9fe6]" /></div>
                    <div className="text-[#2f9fe6] font-display font-bold text-sm">STEP {i + 1}</div>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2">{p.title}</h3>
                  <p className="text-[#6b7683] text-sm">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <Heading center eyebrow="Recent Work" title="Whole-home projects" sub="A few full remodels we've completed across the Edmonton area." />
          <div className="grid md:grid-cols-3 gap-6">
            {PORTFOLIO.map((p, i) => (
              <div key={i} className={`reveal delay-${(i % 3) + 1} bg-white rounded-2xl overflow-hidden border border-[#eef1f4] hover:shadow-xl transition-all`} data-testid={`portfolio-${i}`}>
                <img src={p.img} alt={`${p.area} whole-home remodel`} loading="lazy" className="w-full h-52 object-cover" />
                <div className="p-6">
                  <h3 className="font-display text-lg font-bold text-[#0f2740] mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#2f9fe6]" />{p.area}</h3>
                  <p className="text-[#4a5560] text-sm mb-4">{p.uses}</p>
                  <p className="text-[#6b7683] text-sm mb-4 italic">{p.note}</p>
                  <div className="flex gap-6 text-sm border-t border-[#eef1f4] pt-4">
                    <div className="flex items-center gap-2 text-[#0f2740]"><Ruler className="w-4 h-4 text-[#9aa4af]" />{p.size}</div>
                    <div className="flex items-center gap-2 text-[#0f2740]"><Clock className="w-4 h-4 text-[#9aa4af]" />{p.length}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-24 bg-white">
        <div className="container-x">
          <Heading center eyebrow="Transformation" title="Before & after" sub="From dated and disconnected to bright, open and cohesive." />
          <div className="max-w-3xl mx-auto reveal"><BeforeAfterSlider before={IMG.demo} after={IMG.dining} label="Whole-home transformation" /></div>
        </div>
      </section>

      {/* WHAT TO CONSIDER */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Plan Ahead</span></div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-6">What to consider before you start</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {CONSIDER.map((c) => (
                <div key={c} className="flex items-start gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                  <span className="w-6 h-6 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="text-[#dbe7f1] text-sm">{c}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="reveal-right">
            <div className="shape-soft"><img src={IMG.framing} alt="Home mid-renovation" className="w-full h-[480px] object-cover" /></div>
          </div>
        </div>
      </section>

      {/* COST FACTORS */}
      <section className="py-24 bg-[#f2ede4]">
        <div className="container-x">
          <Heading center eyebrow="Budget" title="What affects a full remodel's cost?" sub="Every home is different. These factors shape your estimate — we'll prepare a detailed, itemized quote for your project." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {COST_FACTORS.map((c, i) => (
              <div key={c} className={`reveal delay-${(i % 4) + 1} flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-[#e5ddce]`}>
                <span className="w-7 h-7 rounded-full bg-[#2f9fe6] flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-white" /></span>
                <span className="font-medium text-[#0f2740] text-sm">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS + TRUST */}
      {reviews && reviews.length > 0 && (
        <section className="py-24 bg-white">
          <div className="container-x">
            <Heading center eyebrow="Trust" title="What our clients say" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {reviews.slice(0, 3).map((r, i) => (
                <div key={r.id || r.name} className={`reveal delay-${(i % 3) + 1} bg-[#f7f9fb] rounded-2xl p-7 border border-[#eef1f4]`}>
                  <div className="flex mb-3">{[...Array(r.rating || 5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-[#2f9fe6] text-[#2f9fe6]" />)}</div>
                  <p className="text-[#4a5560] text-sm leading-relaxed mb-4">“{r.text}”</p>
                  <div className="font-display font-semibold text-[#0f2740]">{r.name}</div>
                  <div className="text-sm text-[#9aa4af]">{r.role}</div>
                </div>
              ))}
            </div>
            <div className="bg-[#f2ede4] rounded-2xl p-8 max-w-3xl mx-auto text-center reveal">
              <h3 className="font-display text-xl font-bold text-[#0f2740] mb-2">Who will manage your project</h3>
              <p className="text-[#5a6570]">A dedicated project lead is your single point of contact from design through final walkthrough — coordinating trades, inspections and timelines so you're never left guessing.</p>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x max-w-3xl">
          <Heading center eyebrow="FAQ" title="Full home remodeling FAQ" />
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div key={i} className="border border-[#e7e9ec] rounded-xl overflow-hidden bg-white">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left" data-testid={`faq-${i}`}>
                  <span className={`font-display font-semibold ${openFaq === i ? "text-[#2f9fe6]" : "text-[#0f2740]"}`}>{f.q}</span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${openFaq === i ? "bg-[#2f9fe6] text-white" : "bg-[#f2f5f8] text-[#0f2740]"}`}>{openFaq === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? "max-h-72" : "max-h-0"}`}><p className="px-6 pb-5 text-[#6b7683] leading-relaxed">{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA + FORM */}
      <section className="py-24 bg-[#0f2740] text-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal-left">
            <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Get Started</span></div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold mb-5">Ready to remodel your whole home?</h2>
            <p className="text-[#a9c6dd] text-lg">Tell us about your home and what you'd like to change. We'll discuss the vision, priorities, likely permit requirements and the next steps for your Edmonton whole-home remodel.</p>
          </div>
          <div className="reveal-right"><FullHomeForm /></div>
        </div>
      </section>
    </>
  );
}
